import React, { useRef } from 'react';

import { useQuery } from 'react-query';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import { fetchFreeSwitchGroups } from '../api';
import type { FreeSwitchGroupListResponse } from '../api';
import { FreeSwitchGroupList, CreateFreeSwitchGroupModal } from '../components';
import type { CreateFreeSwitchGroupModalRef } from '../components';

export const FreeSwitchGroups: React.FC = () => {
  const createModalRef = useRef<CreateFreeSwitchGroupModalRef>(null);

  const { data, isLoading, refetch } = useQuery<FreeSwitchGroupListResponse, Error>(
    'freeswitch-groups',
    fetchFreeSwitchGroups,
    {
      refetchOnWindowFocus: false,
      retry: 1,
      onError: () => {
        Toast.error('Error al cargar los FreeSWITCH Groups');
      },
    }
  );

  const groups = data?.groups ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-white-2.0 bg-primary-2.0 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-lg font-bold text-white-0.0">FreeSWITCH Groups</h1>
              {isLoading ? (
                <p className="text-xs text-white-1.0">Cargando grupos…</p>
              ) : (
                <p className="text-xs text-white-1.0">
                  {groups.length} {groups.length === 1 ? 'grupo' : 'grupos'} ·{' '}
                  {groups.reduce((sum, g) => sum + g.account_count, 0)} cuentas asignadas
                </p>
              )}
            </div>
            {!isLoading && groups.length === 0 && (
              <button
                type="button"
                onClick={() => refetch()}
                className="flex items-center gap-1 rounded-md bg-white/10 px-3 py-1 text-xs text-white-0.0 hover:bg-white/20"
              >
                <Icon name={OrbitIcons.RefreshCw} size={12} />
                Reintentar
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => refetch()}
              className="flex items-center gap-1 rounded-md bg-white/10 px-3 py-1.5 text-xs text-white-0.0 hover:bg-white/20"
              title="Actualizar lista"
            >
              <Icon name={OrbitIcons.RefreshCw} size={14} />
              Actualizar
            </button>
            <button
              type="button"
              onClick={() => createModalRef.current?.show()}
              className="btn btn-secondary flex items-center gap-2"
              title="Crear nuevo grupo compartido"
            >
              <Icon name={OrbitIcons.PlusCircle} size={16} />
              Nuevo Grupo
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <FreeSwitchGroupList
          groups={groups}
          isLoading={isLoading}
          onGroupUpdated={() => refetch()}
        />
      </div>

      <CreateFreeSwitchGroupModal ref={createModalRef} onGroupCreated={() => refetch()} />
    </div>
  );
};
